import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { setQueueDrawerIsOpen, fetchAppMessages } from 'store/actions';
import { Stack, EnvChip, LogoAmherst, DevSettingsMenu, ConnectionChip, ServicesHealthDialog, TabDropdown } from 'components/layout';
import AboutDialog from 'components/about/AboutDialog';
import ProfileDialog from 'components/profile/ProfileDialog';
import { oktaAuth } from 'auth';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Tabs,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
  SvgIcon,
  Popover,
  Button,
  Box
} from '@material-ui/core';

import { userRoleSourcingUserSelector, mainAppMessageSelector } from 'store/selectors';
import {
  ExitToApp as ExitToAppIcon,
  Info as InfoIcon,
  Menu as MenuIcon,
  MoreVert as MoreVertIcon,
  SignalCellularAlt as SignalCellularAltIcon,
  Map as MapIcon,
  CreateNewFolder as CreateNewFolderIcon,
  ThumbsUpDown as ThumbsUpDownIcon,
  Dashboard as DashboardIcon,
  Timeline as TimelineIcon,
  BusinessCenter as BusinessCenterIcon,
  Public as PublicIcon,
  MonetizationOn as MonetizationOnIcon,
  PieChart as PieChartIcon,
  Search as SearchIcon
} from '@material-ui/icons';
import { sendClientLog } from 'services/logService';
import { Search } from 'components/search';
import { Constants, getModuleByLocation } from 'utilities/constants';
import { useSaveUWDialog } from 'components/underwrite/shared/SaveUWDialog';

const UnderwriteIcon = () => (
  <SvgIcon>
    <path d="M11 8C11 10.21 9.21 12 7 12C4.79 12 3 10.21 3 8C3 5.79 4.79 4 7 4C9.21 4 11 5.79 11 8M11 14.72V20H0V18C0 15.79 3.13 14 7 14C8.5 14 9.87 14.27 11 14.72M24 20H13V3H24V20M16 11.5C16 10.12 17.12 9 18.5 9C19.88 9 21 10.12 21 11.5C21 12.88 19.88 14 18.5 14C17.12 14 16 12.88 16 11.5M22 7C20.9 7 20 6.11 20 5H17C17 6.11 16.11 7 15 7V16C16.11 16 17 16.9 17 18H20C20 16.9 20.9 16 22 16V7Z" />
  </SvgIcon>
)

const TitleBar = ({ history, location, classes, exploreImportSelectedTab, underwriteImportSelectedTab, postAcqImportSelectedTab,
  user, setQueueDrawerIsOpen, valuationIsDirty, userHasSourcingUserRole, fetchAppMessages, appMessages,
  mainAppMessage, defaultColor, isMobile }) => {

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [servicesHealthOpen, setServicesHealthOpen] = useState(false);
  const userHasExploreRole = user.roles.exploreUser;
  const userHasUnderwriteRole = user.roles.underwriteUser;
  const userHasAdminRole = user.roles.administrator;
  const [appMessageAnchor, setAppMessageAnchor] = useState(null);
  const [menuOpened, setMenuOpened] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const { openSaveUWDialog } = useSaveUWDialog();

  useEffect(() => {
    fetchAppMessages();
  }, []);

  const toggleAppMessageAnchor = (e) => {
    const el = appMessageAnchor ? null : e.currentTarget;
    setAppMessageAnchor(el);
  }

  // const [path, setPath] = useState('underwrite');

  // useEffect(() => {
  //   const { pathname } = location;
  //   const [, _path] = pathname.split('/');
  //   const tabs = ['workflow', 'underwrite', 'import'];
  //   if (tabs.includes(_path)) {       // validate path, the user may type in the wrong route
  //     setPath(_path);
  //   } else {
  //     setPath('workflow');          // default to workflow if path is invalid
  //   }
  // }, [location]);

  const pathSegment = location.pathname.split('/')[1];
  const isAdmin = location.pathname.startsWith(Constants.Routes.ADMINISTRATION);
  const isLogin = location.pathname.startsWith(Constants.Routes.LOGIN_PATH)
  // Admin button lives in different menu, so ignore it. Also ignore login callback paths
  const path = isAdmin || isLogin ? false : pathSegment;

  const title = 'ReVUe';
  const titleBarHeight = 48;

  const toggleMenu = e => {
    if (!!menuAnchor) setMenuAnchor(null);
    else setMenuAnchor(e.currentTarget);
  };

  const handleAboutClick = () => {
    setMenuAnchor(null);
    setAboutOpen(true);
  };

  const handleProfileClick = () => {
    setProfileOpen(true);
  };

  const handleAdministrationClick = () => {
    setMenuAnchor(null);
    handleMenuItemClick(Constants.Routes.ADMINISTRATION);
  };

  const handleAssumptionsClick = () => {
    setMenuAnchor(null);
    handleMenuItemClick(Constants.Routes.ASSUMPTIONS);
  };

  const handleServicesHealthClick = () => {
    setMenuAnchor(null);
    setServicesHealthOpen(true);
  };

  const isPostAcqModule = location.pathname.startsWith(Constants.Routes.POSTACQ);
  const QueueDrawerMenu = () => {
    return (
      <Tooltip title='Toggle Queue (Ctrl + Q)'>
        <IconButton className={classes.toggleDrawerButton} aria-label='Open Queue Drawer' onClick={() => setQueueDrawerIsOpen()}>
          <MenuIcon />
        </IconButton>
      </Tooltip>
    )
  }

  const handleMenuItemClick = (route) => {
    if (valuationIsDirty) {
      openSaveUWDialog({
        saveUWDialogParameters: { route }
      });
    } else {
      history.push(route);
    }
  }

  const onLogout = () => {
    setMenuAnchor(null);
    sendClientLog('Info', 'User logged out via menu', () => oktaAuth.signOut({ clearTokensBeforeRedirect: true })); // Must clear before redireect or else will return to app, in 'Session Expired' status

    setTimeout(() => oktaAuth.signOut({ clearTokensBeforeRedirect: true }), 1000); // In case sendClientLog API call takes too long to fire callback, we log out the user anyway.
  };

  const exploreMenuItems = [];
  if (userHasExploreRole) exploreMenuItems.push({ label: 'Map', route: Constants.Routes.EXPLORE, icon: <MapIcon /> });
  if (userHasSourcingUserRole) {
    exploreMenuItems.push({ label: 'Prospects', route: Constants.Routes.EXPLORE_CONDUCTOR, icon: <ThumbsUpDownIcon /> });
    exploreMenuItems.push({
      label: 'Import',
      // Construct url with selected tab. Ex 'explore/import/status'
      route: exploreImportSelectedTab
        ? Constants.Routes.EXPLORE_IMPORT + '/' + exploreImportSelectedTab
        : Constants.Routes.EXPLORE_IMPORT
      , icon: <CreateNewFolderIcon />
    });
  }
  const isUnderwriteModule = path === Constants.Module.UNDERWRITE;

  const appMessagesItems = appMessages.map((msg, index) =>
    <div key={msg.id} className={index === appMessages.length - 1 ? classes.messageInPopupNoBorder : classes.messageInPopup}>
      <div className={classes.circleInPopup} style={{ backgroundColor: msg.color }} />
      <Typography align='left' color='inherit' style={{ marginLeft: 20 }} >
        {msg.body?.length > 0 ? msg.body : msg.subject}
      </Typography>
    </div>
  );

  function handleMenuOpen(menu) {
    setMenuOpened(menu);
  }

  return (isMobile) ? (
    <>
      <AppBar position="static">
        <Toolbar noGutters>
          <EnvChip />
          <Stack horizontal grow className={classes.center}>
            <Tabs value={path === Constants.Module.POSTACQ ? Constants.Module.UNDERWRITE : path}>
              {(userHasExploreRole || userHasSourcingUserRole) &&
                <TabDropdown
                  tabClasses={{ root: classes.tabRootIcon }}
                  value='explore'
                  menuItems={exploreMenuItems}
                  handleMenuItemClick={handleMenuItemClick}
                  handleMenuOpen={handleMenuOpen}
                  isMenuOpen={menuOpened === 'explore'}
                  icon={
                    <Tooltip title='Explore'>
                      <PublicIcon className={classes.mobileIcon} />
                    </Tooltip>
                  }
                />
              }
              {userHasUnderwriteRole &&
                <TabDropdown
                  tabClasses={{ root: classes.tabRootIcon }}
                  value='underwrite'
                  handleMenuOpen={handleMenuOpen}
                  menuItems={[
                    { subheader: 'Acquisitions' },
                    { label: 'Workflow', route: Constants.Routes.CHANNEL_WORKFLOW, icon: <DashboardIcon /> },
                    { label: 'Underwrite', route: Constants.Routes.UNDERWRITE, icon: <UnderwriteIcon /> },
                    {
                      label: 'Import',
  
                      // Construct url with selected tab. Ex 'explore/underwrite/status'
                      route: `${underwriteImportSelectedTab
                        ? Constants.Routes.UNDERWRITE_IMPORT + '/' + underwriteImportSelectedTab
                        : Constants.Routes.UNDERWRITE_IMPORT}`
                      , icon: <CreateNewFolderIcon />
                    },
                    { subheader: 'Post-Acquisitions' },
                    { label: 'Workflow', route: Constants.Routes.POSTACQ_WORKFLOW, icon: <DashboardIcon /> },
                    { label: 'Underwrite', route: Constants.Routes.POSTACQ, icon: <UnderwriteIcon /> }
                  ]}
                  handleMenuItemClick={handleMenuItemClick}
                  isMenuOpen={menuOpened === 'underwrite'}
                  icon={
                    <Tooltip title='Underwrite'>
                      <MonetizationOnIcon className={classes.mobileIcon} />
                    </Tooltip>
                  }
                />
              }
              <TabDropdown
                tabClasses={{ root: classes.tabRootIcon }}
                value='dashboard'
                handleMenuOpen={handleMenuOpen}
                isMenuOpen={menuOpened === 'dashboard'}
                menuItems={[
                  { label: 'Dashboard', route: Constants.Routes.DASHBOARD, icon: <TimelineIcon /> },
                  { label: 'Search', route: Constants.Routes.DASHBOARD_SEARCH, icon: <SearchIcon /> },
                ]}
                handleMenuItemClick={handleMenuItemClick}
                icon={
                  <Tooltip title='Dashboard'>
                    <PieChartIcon className={classes.mobileIcon} />
                  </Tooltip>
                }
              />
            </Tabs>
            <ConnectionChip inHeader={true} />
            <DevSettingsMenu />
          </Stack>
          <Box sx={{ flexGrow: 0 }}>
            <IconButton color='inherit' onClick={toggleMenu}>
              <MoreVertIcon />
            </IconButton>
            <Menu id='title-bar-menu' anchorEl={menuAnchor} open={!!menuAnchor} onClose={toggleMenu}>
              <MenuItem onClick={handleAboutClick}>
                <ListItemIcon className={classes.listItemIcon}>
                  <InfoIcon />
                </ListItemIcon>
                <ListItemText primary={`About ${title}`} />
              </MenuItem>
              <MenuItem onClick={handleServicesHealthClick}>
                <ListItemIcon className={classes.listItemIcon}>
                  <SignalCellularAltIcon />
                </ListItemIcon>
                <ListItemText primary={`Services Status`} />
              </MenuItem>
              <Divider />

              {userHasAdminRole &&
                <MenuItem onClick={handleAdministrationClick}>
                  <ListItemIcon className={classes.listItemIcon}>
                    <BusinessCenterIcon />
                  </ListItemIcon>
                  <ListItemText primary={`Administration`} />
                </MenuItem>
              }
              <MenuItem onClick={handleAssumptionsClick}>
                <ListItemIcon className={classes.listItemIcon}>
                  <DashboardIcon />
                </ListItemIcon>
                <ListItemText primary={`Assumptions`} />
              </MenuItem>
              <Divider />
              <MenuItem onClick={onLogout}>
                <ListItemIcon className={classes.listItemIcon}>
                  <ExitToAppIcon />
                </ListItemIcon>
                <ListItemText primary='Log Out' />
              </MenuItem>
            </Menu>
            <AboutDialog open={aboutOpen} onClose={() => setAboutOpen(false)} user={user}/>            
            <ServicesHealthDialog open={servicesHealthOpen} onClose={() => setServicesHealthOpen(false)} />
          </Box>
        </Toolbar>
      </AppBar>
    </>
  ) : (
    <AppBar position='static'>
      <Toolbar variant='dense'>
        <Stack horizontal className={classes.menuContainer} style={{ height: titleBarHeight }}>
          {userHasUnderwriteRole && (isUnderwriteModule || isPostAcqModule)
            ? <QueueDrawerMenu />
            : <></>
          }
        </Stack>
        <Stack horizontal className={classes.left} style={{ height: titleBarHeight }}>
          <LogoAmherst height={titleBarHeight} />
        </Stack>
        {/* Center */}
        <Stack horizontal grow className={classes.center}>
          <EnvChip />
          <Typography className={classes.title} variant='h6' color='inherit'>
            {title}
          </Typography>

          <Tabs classes={{ root: classes.tabsRoot, indicator: classes.tabIndicator }} value={path === Constants.Module.POSTACQ ? Constants.Module.UNDERWRITE : path}>
            {(userHasExploreRole || userHasSourcingUserRole) &&
              <TabDropdown
                tabClasses={{ root: classes.tabRoot, wrapper: classes.tabWrapper }}
                label={'Explore'}
                value='explore'
                menuItems={exploreMenuItems}
                handleMenuItemClick={handleMenuItemClick}
                handleMenuOpen={handleMenuOpen}
                isMenuOpen={menuOpened === 'explore'}
              />
            }
            {userHasUnderwriteRole &&
              <TabDropdown
                tabClasses={{ root: classes.tabRoot, wrapper: classes.tabWrapper }}
                label={'Underwrite'}
                value='underwrite'
                handleMenuOpen={handleMenuOpen}
                menuItems={[
                  { subheader: 'Acquisitions' },
                  { label: 'Workflow', route: Constants.Routes.CHANNEL_WORKFLOW, icon: <DashboardIcon /> },
                  { label: 'Underwrite', route: Constants.Routes.UNDERWRITE, icon: <UnderwriteIcon /> },
                  {
                    label: 'Import',

                    // Construct url with selected tab. Ex 'explore/underwrite/status'
                    route: `${underwriteImportSelectedTab
                      ? Constants.Routes.UNDERWRITE_IMPORT + '/' + underwriteImportSelectedTab
                      : Constants.Routes.UNDERWRITE_IMPORT}`
                    , icon: <CreateNewFolderIcon />
                  },
                  { subheader: 'Post-Acquisitions' },
                  { label: 'Workflow', route: Constants.Routes.POSTACQ_WORKFLOW, icon: <DashboardIcon /> },
                  { label: 'Underwrite', route: Constants.Routes.POSTACQ, icon: <UnderwriteIcon /> },
                  { label: 'Import', route: `${postAcqImportSelectedTab
                      ? Constants.Routes.POSTACQ_IMPORT + '/' + postAcqImportSelectedTab
                      : Constants.Routes.POSTACQ_IMPORT}`
                    , icon: <CreateNewFolderIcon />
                  }
                ]}
                handleMenuItemClick={handleMenuItemClick}
                isMenuOpen={menuOpened === 'underwrite'}
              />
            }
            {
              // User will be able to see Dashboard dropdown and navigate to Dashboard page as long as they have 'Access' role (can login)
              <TabDropdown
                tabClasses={{ root: classes.tabRoot, wrapper: classes.tabWrapper }}
                label={'Dashboard'}
                value='dashboard'
                handleMenuOpen={handleMenuOpen}
                isMenuOpen={menuOpened === 'dashboard'}
                menuItems={[
                  { label: 'Dashboard', route: Constants.Routes.DASHBOARD, icon: <TimelineIcon /> },
                  { label: 'Search', route: Constants.Routes.DASHBOARD_SEARCH, icon: <SearchIcon /> },
                ]}
                handleMenuItemClick={handleMenuItemClick}
              />
            }
          </Tabs>
          <DevSettingsMenu />
        </Stack>

        {/* Right */}
        <Stack horizontal className={classes.right}>
          {appMessages && appMessages.length > 0 &&
            [
              <Button key='appMessagesButton'
                size='small'
                variant='contained'
                style={{
                  backgroundColor: mainAppMessage?.color ?? defaultColor,
                  color: 'white',
                  marginRight: 20,
                  borderRadius: 25,
                  textTransform: 'none', // removes capital case
                  width: 200,
                  height: 20
                }}
                onClick={toggleAppMessageAnchor}>
                <Typography style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: 'nowrap'
                }}>
                  {mainAppMessage?.subject}
                </Typography>
              </Button>
              ,
              <Popover key='appMessagesPopover'
                open={Boolean(appMessageAnchor)}
                anchorEl={appMessageAnchor}
                onClose={toggleAppMessageAnchor}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}>

                <div style={{ margin: 10, maxWidth: 600 }}>
                  {appMessagesItems}
                </div>

              </Popover>
            ]
          }

          {userHasUnderwriteRole && (isUnderwriteModule || isPostAcqModule) &&
            <Search />
          }
          <ConnectionChip inHeader={true} />
          <Typography variant='button' color='inherit' className={classes.userName} onClick={handleProfileClick}>
            {user ? user.fullName || user.name : ''}
          </Typography>
          <IconButton color='inherit' onClick={toggleMenu}>
            <MoreVertIcon />
          </IconButton>
          <ProfileDialog open={profileOpen} onClose={() => setProfileOpen(false)} user={user} />
        </Stack>

        {/* Menu attached to Right MoreVert */}
        <Menu id='title-bar-menu' anchorEl={menuAnchor} open={!!menuAnchor} onClose={toggleMenu}>
          <MenuItem onClick={handleAboutClick}>
            <ListItemIcon className={classes.listItemIcon}>
              <InfoIcon />
            </ListItemIcon>
            <ListItemText primary={`About ${title}`} />
          </MenuItem>
          <MenuItem onClick={handleServicesHealthClick}>
            <ListItemIcon className={classes.listItemIcon}>
              <SignalCellularAltIcon />
            </ListItemIcon>
            <ListItemText primary={`Services Status`} />
          </MenuItem>
          <Divider />

          {userHasAdminRole &&
            <MenuItem onClick={handleAdministrationClick}>
              <ListItemIcon className={classes.listItemIcon}>
                <BusinessCenterIcon />
              </ListItemIcon>
              <ListItemText primary={`Administration`} />
            </MenuItem>
          }
          <MenuItem onClick={handleAssumptionsClick}>
            <ListItemIcon className={classes.listItemIcon}>
              <DashboardIcon />
            </ListItemIcon>
            <ListItemText primary={`Assumptions`} />
          </MenuItem>
          <Divider />
          <MenuItem onClick={onLogout}>
            <ListItemIcon className={classes.listItemIcon}>
              <ExitToAppIcon />
            </ListItemIcon>
            <ListItemText primary='Log Out' />
          </MenuItem>
        </Menu>
        <AboutDialog open={aboutOpen} onClose={() => setAboutOpen(false)} user={user} isMobile={isMobile}/>
        <ServicesHealthDialog open={servicesHealthOpen} onClose={() => setServicesHealthOpen(false)} />
      </Toolbar>
    </AppBar>
  );
}

const styles = theme => ({
  menuContainer: {
    marginLeft: -20,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  left: {
    marginLeft: 4,
    marginRight: 0
  },
  center: {
    alignItems: 'center'
  },
  right: {
    alignItems: 'center',
    marginRight: -12,
    marginLeft: 16
  },
  listItemIcon: {
    minWidth: 40
  },
  userName: {
    marginRight: '8px !important',
    textAlign: 'center',
    lineHeight: 'initial',
    cursor: 'pointer'
  },
  title: {
    marginLeft: theme.spacing(2)
  },
  tabsRoot: {
    margin: `0 ${theme.spacing(3)}px`,
    backgroundColor: '#fff'
  },
  tabIndicator: {
    bottom: 4
  },
  tabRoot: {
    minWidth: 0,
    opacity: 1,
    padding: '6px 24px',
    backgroundColor: theme.palette.primary.main
  },
  tabRootIcon: {
    minWidth: 0,
    opacity: 1,
    backgroundColor: theme.palette.primary.main,
    [theme.breakpoints.down(374)]: {
      padding: '6px 8px'
    }
  },
  tabWrapper: {
    whiteSpace: 'nowrap'
  },
  toggleDrawerButton: {
    color: 'inherit'
  },
  circleInPopup: {
    position: 'absolute',
    borderRadius: '50%',
    width: 14,
    height: 14,
    marginTop: '3px'
  },
  messageInPopup: {
    display: 'flex',
    flexDirection: 'row',
    borderBottom: 'groove',
    padding: '3px 10px',
    marginBottom: '3px'
  },
  messageInPopupNoBorder: {
    display: 'flex',
    flexDirection: 'row',
    padding: '3px 10px',
    marginBottom: '3px'
  },
  mobileIcon: {
    [theme.breakpoints.down(374)]: {
      fontSize: '1.25rem'
    }
  }
});

const mapStateToProps = (state, ownProps) => {
  const moduleState = ownProps.location.pathname.startsWith(Constants.Routes.POSTACQ) ? state.postAcqUWState : state.propertyUWState;
  return {
    exploreImportSelectedTab: state.importState[Constants.Module.EXPLORE].importSelectedTab,
    underwriteImportSelectedTab: state.importState[Constants.Module.UNDERWRITE].importSelectedTab,
    postAcqImportSelectedTab: state.importState[Constants.Module.POSTACQ].importSelectedTab,
    user: state.appState.user,
    valuationIsDirty: moduleState.valuationIsDirty,
    userHasSourcingUserRole: userRoleSourcingUserSelector(state),
    appMessages: state.adminState.appMessages,
    mainAppMessage: mainAppMessageSelector(state),
    defaultColor: state.adminState.appMessageColors[0].value,
    isMobile: state.appState.isMobile,
  }
};
const mapDispatchToProps = (dispatch, ownProps) => {
  const module = getModuleByLocation(ownProps.location);
  return {
    setQueueDrawerIsOpen: isOpen => dispatch(setQueueDrawerIsOpen(module, isOpen)),
    fetchAppMessages: () => dispatch(fetchAppMessages({}))
  }
};

export default compose(
  withRouter,
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps)
)(TitleBar);
